import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { TrendingUp, Heart, Award, Calendar, Loader2, CheckCircle, Flame, Sun, Moon, Smile, Frown, Meh } from 'lucide-react';
import { apiClient } from '../api/client';

const MOOD_OPTIONS = [
  { score: 0.2, label: 'Struggling', icon: Frown, color: 'text-red-500', bg: 'bg-red-50 border-red-200 hover:bg-red-100' },
  { score: 0.4, label: 'Low', icon: Frown, color: 'text-orange-500', bg: 'bg-orange-50 border-orange-200 hover:bg-orange-100' },
  { score: 0.6, label: 'Okay', icon: Meh, color: 'text-yellow-500', bg: 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100' },
  { score: 0.8, label: 'Good', icon: Smile, color: 'text-green-500', bg: 'bg-green-50 border-green-200 hover:bg-green-100' },
  { score: 1.0, label: 'Great', icon: Sun, color: 'text-emerald-500', bg: 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100' },
];

const Wellness = () => {
  const [moodHistory, setMoodHistory] = useState([]);
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkinDone, setCheckinDone] = useState(false);
  const [selectedMood, setSelectedMood] = useState(null);
  const [gratitude, setGratitude] = useState('');
  const [goalForToday, setGoalForToday] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [checkinResult, setCheckinResult] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [moodRes, streakRes] = await Promise.all([
        apiClient.request('/api/dashboard/mood/history?days=30').catch(() => []),
        apiClient.request('/api/dashboard/streak').catch(() => null),
      ]);
      setMoodHistory(moodRes || []);
      if (streakRes) {
        setStreak(streakRes);
        setCheckinDone(streakRes.checked_in_today);
      }
    } catch { /* silent */ }
    setLoading(false);
  };

  const handleCheckin = async () => {
    if (!selectedMood) return;
    setSubmitting(true);
    try {
      const res = await apiClient.request('/api/dashboard/checkin', {
        method: 'POST',
        body: JSON.stringify({
          mood_score: selectedMood.score,
          mood_label: selectedMood.label,
          gratitude: gratitude || null,
          goal_for_today: goalForToday || null,
        }),
      });
      setCheckinResult(res);
      setCheckinDone(true);
      loadData();
    } catch (err) {
      setCheckinResult({ success: false, message: err.message || 'Check-in failed' });
    }
    setSubmitting(false);
  };

  const recentMoods = moodHistory.filter(m => m.mood_score !== null).slice(-7);
  const avgMood = recentMoods.length > 0
    ? (recentMoods.reduce((sum, m) => sum + m.mood_score, 0) / recentMoods.length)
    : null;

  const getMoodColor = (score) => {
    if (score >= 0.8) return 'bg-emerald-400';
    if (score >= 0.6) return 'bg-yellow-400';
    if (score >= 0.4) return 'bg-orange-400';
    return 'bg-red-400';
  };

  const getMoodLabel = (score) => {
    if (score >= 0.8) return 'Great';
    if (score >= 0.6) return 'Okay';
    if (score >= 0.4) return 'Low';
    return 'Struggling';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-8 flex items-center justify-center min-h-[400px]">
          <Loader2 size={32} className="animate-spin text-purple-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-5xl mx-auto" data-testid="wellness-page">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">My Readiness</h1>
        <p className="text-gray-600 mb-8">How ready are you to connect with real people today?</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Daily Check-in */}
          <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm md:col-span-2" data-testid="daily-checkin">
            <div className="flex items-center gap-3 mb-4">
              <Calendar size={24} className="text-blue-500" />
              <h3 className="text-lg font-bold text-gray-900">Daily Check-In</h3>
              {checkinDone && (
                <span className="ml-auto flex items-center gap-1 text-sm text-green-600 font-medium">
                  <CheckCircle size={16} /> Done today
                </span>
              )}
            </div>

            {checkinDone ? (
              <div className="text-center py-4">
                {checkinResult ? (
                  <div className="space-y-2">
                    <CheckCircle size={40} className="mx-auto text-green-500" />
                    <p className="font-semibold text-gray-900">{checkinResult.message}</p>
                    {checkinResult.hearts_earned > 0 && (
                      <p className="text-sm text-purple-600 font-medium">+{checkinResult.hearts_earned} Hearts earned!</p>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500">You&apos;ve already checked in today. Come back tomorrow!</p>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-3">How are you feeling today?</p>
                  <div className="flex gap-2 flex-wrap">
                    {MOOD_OPTIONS.map((mood) => {
                      const Icon = mood.icon;
                      const isSelected = selectedMood?.label === mood.label;
                      return (
                        <button
                          key={mood.label}
                          onClick={() => setSelectedMood(mood)}
                          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                            isSelected ? `${mood.bg} ring-2 ring-offset-1 ring-purple-400` : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                          }`}
                          data-testid={`mood-${mood.label.toLowerCase()}`}
                        >
                          <Icon size={18} className={isSelected ? mood.color : 'text-gray-400'} />
                          {mood.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">What are you grateful for? (optional)</label>
                  <input
                    type="text"
                    value={gratitude}
                    onChange={(e) => setGratitude(e.target.value)}
                    placeholder="Something good in your day..."
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-300 focus:border-transparent text-sm"
                    data-testid="gratitude-input"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Social goal for today (optional)</label>
                  <input
                    type="text"
                    value={goalForToday}
                    onChange={(e) => setGoalForToday(e.target.value)}
                    placeholder="e.g., Say hi to someone new"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-300 focus:border-transparent text-sm"
                    data-testid="goal-input"
                  />
                </div>

                <button
                  onClick={handleCheckin}
                  disabled={!selectedMood || submitting}
                  className="w-full py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="checkin-submit"
                >
                  {submitting ? <><Loader2 size={16} className="animate-spin" /> Checking in...</> : 'Complete Check-In'}
                </button>
              </div>
            )}
          </div>

          {/* Streak Card */}
          <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="streak-card">
            <div className="flex items-center gap-3 mb-4">
              <Flame size={24} className="text-orange-500" />
              <h3 className="text-lg font-bold text-gray-900">Readiness Streak</h3>
            </div>
            {streak ? (
              <div className="space-y-4">
                <div className="text-center">
                  <p className="text-5xl font-bold text-gray-900">{streak.current_streak}</p>
                  <p className="text-sm text-gray-500 mt-1">day{streak.current_streak !== 1 ? 's' : ''} in a row</p>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Next milestone</span>
                    <span className="font-semibold text-purple-600">Day {streak.next_milestone}</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-orange-400 to-purple-500 rounded-full transition-all"
                      style={{ width: `${Math.min(100, (streak.current_streak / streak.next_milestone) * 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-400 text-right">+{streak.milestone_reward} Hearts at milestone</p>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">Start checking in daily to build your streak!</p>
            )}
          </div>

          {/* Mood Tracking */}
          <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="mood-tracking">
            <div className="flex items-center gap-3 mb-4">
              <Heart size={24} className="text-red-500" />
              <h3 className="text-lg font-bold text-gray-900">Mood Trends (30 days)</h3>
            </div>
            {recentMoods.length > 0 ? (
              <div className="space-y-4">
                {/* Average Mood */}
                {avgMood !== null && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <div className={`w-10 h-10 rounded-full ${getMoodColor(avgMood)} flex items-center justify-center`}>
                      <Smile size={20} className="text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Average mood</p>
                      <p className="font-bold text-gray-900">{getMoodLabel(avgMood)}</p>
                    </div>
                  </div>
                )}

                {/* Mini mood chart */}
                <div className="flex items-end gap-1 h-20">
                  {moodHistory.slice(-14).map((m, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <div
                        className={`w-full rounded-sm transition-all ${m.mood_score ? getMoodColor(m.mood_score) : 'bg-gray-100'}`}
                        style={{ height: m.mood_score ? `${m.mood_score * 100}%` : '10%' }}
                        title={m.mood_score ? `${m.mood_label} (${m.date})` : `No data (${m.date})`}
                      />
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-400 text-center">Last 14 days</p>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No mood data yet. Complete your first check-in above!</p>
            )}
          </div>

          {/* Analytics Dashboard */}
          <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="analytics-card">
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp size={24} className="text-purple-500" />
              <h3 className="text-lg font-bold text-gray-900">Analytics</h3>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">Total check-ins</span>
                <span className="font-bold text-gray-900">{recentMoods.length}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">Current streak</span>
                <span className="font-bold text-gray-900">{streak?.current_streak || 0} days</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">Best mood</span>
                <span className="font-bold text-gray-900">
                  {recentMoods.length > 0 ? getMoodLabel(Math.max(...recentMoods.map(m => m.mood_score))) : '--'}
                </span>
              </div>
            </div>
          </div>

          {/* Milestones Preview */}
          <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="milestones-preview">
            <div className="flex items-center gap-3 mb-4">
              <Award size={24} className="text-yellow-500" />
              <h3 className="text-lg font-bold text-gray-900">Streak Milestones</h3>
            </div>
            <div className="space-y-2">
              {[7, 14, 30, 60, 90].map(day => {
                const reached = (streak?.current_streak || 0) >= day;
                return (
                  <div key={day} className={`flex items-center justify-between p-2.5 rounded-lg ${reached ? 'bg-green-50' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${reached ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                        {reached ? <CheckCircle size={14} /> : day}
                      </div>
                      <span className={`text-sm ${reached ? 'text-green-800 font-medium' : 'text-gray-600'}`}>
                        {day}-Day Streak
                      </span>
                    </div>
                    <span className="text-xs font-bold text-purple-600">
                      +{day >= 30 ? 100 : 25} Hearts
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Wellness;
